import React, { useMemo } from 'react';
import { ApiAlertedEntity, ApiAttributeMapping, ApiPersonCategory, ApiPersonType } from '../../api';
import { faBriefcase, faMoneyBillTransfer, faUser, faUserNinja } from '@fortawesome/pro-solid-svg-icons';
import { faUserAlien } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPersonDisplayName } from '../../common/helper';
import { formatAmount } from '../../schema/helper';
import { Link } from 'react-router-dom';
import { InternalApiCurrentUserOrganisationCurrencyEnum } from '../../api-internal';

interface Props {
  entity: ApiAlertedEntity;
  mappings: { [key: string]: ApiAttributeMapping };
  organisationCurrency?: InternalApiCurrentUserOrganisationCurrencyEnum;
}

const getPersonIcon = (personCategory: ApiPersonCategory | undefined, personType: ApiPersonType | undefined) => {
  if (personCategory === ApiPersonCategory.COUNTERPARTY) {
    return faUserAlien;
  }
  if (personType === ApiPersonType.INDIVIDUAL || personType === ApiPersonType.PRIVATE) {
    return faUser;
  }
  if (personType === ApiPersonType.BUSINESS || personType === ApiPersonType.LEGAL) {
    return faBriefcase;
  }
  return faUserNinja;
};

const AlertedEntityDetails = ({ entity, mappings, organisationCurrency }: Props) => {
  const { person, transaction }: ApiAlertedEntity = entity;

  const entityDescription = useMemo(() => {
    if (person?.attributes) {
      return <Link to={`/customer/${person.id}`}>{getPersonDisplayName(person, mappings)}</Link>;
    } else if (person) {
      return <Link to={`/customer/${person.id}`}>{person.id}</Link>;
    } else if (transaction) {
      return <Link to={`/transactions/${transaction!.id}`}>{transaction.id}</Link>;
    } else {
      return '';
    }
  }, []);

  const transactionDetails = transaction && (
    <>
      {' • '}
      <Link to={`/transactions/${transaction!.id}`}>
        <FontAwesomeIcon icon={faMoneyBillTransfer} />{' '}
        {formatAmount(parseFloat(transaction!.attributes?.amount || '0'), organisationCurrency)}
      </Link>
    </>
  );

  const entityIcon = useMemo(() => {
    const icon = entity.person ? getPersonIcon(entity.person?.category, entity.person?.type) : faUserNinja;
    return <FontAwesomeIcon icon={icon} aria-label={icon.iconName} className='link-color' />;
  }, []);

  return (
    <>
      <span className='link-color'>
        {entityIcon} {entityDescription}
      </span>
      {transactionDetails}
    </>
  );
};

export default AlertedEntityDetails;
